import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LogOut, Loader2, AlertTriangle, Truck, MapPin, Trophy, TrendingDown,
  ChevronRight, Calendar, Award,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface DashboardData {
  driver: { id: string; code: string | null; fullNameEn: string | null; fullNameUr: string | null; photoUrl: string | null; phone: string | null };
  vehicle: { id: string; plate: string | null; type: string | null; brand: string | null; model: string | null; siteId: string | null; siteName: string | null } | null;
  thisMonth: { trips: number; revenueSar: number; driverPaySar: number; deliveredQty: number; totalKm: number };
  recentTrips: { id: string; code: string; tripDate: string; status: string; deliveredQty: number; unit: string; kmAllocated: number | null; variancePct: number | null; consumptionStatus: string | null; originSite: string | null; destinationSite: string | null }[];
  performance: { compositeScore: number | null; scoreConsumption: number | null; scoreIdle: number | null; scoreDrivingStyle: number | null; scoreSchedule: number | null; scoreMaintenance: number | null; periodStart: string; periodEnd: string; totalTrips: number; totalKm: number; wasteCostSar: number } | null;
  leaderboard: { rank: number; total: number } | null;
  publicLeaderboard: boolean;
  personalRecords: { bestEfficiency: number | null; bestEfficiencyTrip: string | null; longestKm: number | null; longestKmTrip: string | null; mostTripsInDay: number };
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function tier(s: number | null): { label: string; color: string } {
  if (s === null) return { label: 'No data', color: 'text-muted-foreground' };
  if (s >= 90) return { label: 'Excellent', color: 'text-emerald-600 dark:text-emerald-400' };
  if (s >= 75) return { label: 'Good',      color: 'text-sky-600 dark:text-sky-400' };
  if (s >= 60) return { label: 'Coaching',  color: 'text-amber-600 dark:text-amber-400' };
  if (s >= 40) return { label: 'Review',    color: 'text-orange-600 dark:text-orange-400' };
  return { label: 'Investigate', color: 'text-rose-600 dark:text-rose-400' };
}

export default function DriverSelfView() {
  const { user, logout } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get<DashboardData>('/api/v1/driver-portal/me/dashboard')
      .then((r) => setData(r.data))
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground text-center max-w-sm">
        {error ?? 'No driver record linked to your account'}
      </div>
      <Button variant="outline" onClick={logout} className="gap-2">
        <LogOut className="h-4 w-4" /> Sign out
      </Button>
    </div>
  );

  const t = tier(data.performance?.compositeScore ?? null);
  const initials = (data.driver.fullNameEn ?? data.driver.code ?? '?').split(' ').map((p) => p[0]).slice(0, 2).join('');

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-base font-medium text-muted-foreground overflow-hidden">
              {data.driver.photoUrl ? (
                <img src={data.driver.photoUrl} alt="" className="h-12 w-12 object-cover" />
              ) : initials}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{data.driver.fullNameEn ?? data.driver.code ?? '—'}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5 truncate">
                {data.vehicle ? <><Truck className="h-3 w-3" /> {data.vehicle.plate ?? '—'}</> : 'No vehicle'}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-2"><LogOut className="h-4 w-4" /> Sign out</Button>
        </div>
        {data.vehicle?.siteName && (
          <div className="max-w-md mx-auto px-4 pb-2 text-xs text-muted-foreground inline-flex items-center gap-1.5">
            <MapPin className="h-3 w-3" /> {data.vehicle.siteName}
          </div>
        )}
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4">
        {/* Performance */}
        {data.performance && (
          <div className="rounded-lg border border-border p-5 text-center space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Performance</div>
            <div className={'text-5xl font-display ' + t.color}>{data.performance.compositeScore !== null ? data.performance.compositeScore.toFixed(0) : '—'}</div>
            <div className={'text-sm ' + t.color}>{t.label}</div>
            {data.leaderboard && (
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1 pt-1">
                <Trophy className="h-3 w-3" /> Ranked #{data.leaderboard.rank} of {data.leaderboard.total}
              </div>
            )}
            {/* Sub-score mini bars */}
            <div className="grid grid-cols-5 gap-2 pt-3">
              {[
                { l: 'Cons',  s: data.performance.scoreConsumption },
                { l: 'Idle',  s: data.performance.scoreIdle },
                { l: 'Style', s: data.performance.scoreDrivingStyle },
                { l: 'Sched', s: data.performance.scoreSchedule },
                { l: 'Maint', s: data.performance.scoreMaintenance },
              ].map((x) => (
                <div key={x.l} className="text-center">
                  <div className="h-1.5 bg-muted rounded overflow-hidden mb-1">
                    <div className={'h-full ' + barColor(x.s)} style={{ width: (x.s ?? 0) + '%' }} />
                  </div>
                  <div className="text-[10px] text-muted-foreground">{x.l}</div>
                  <div className="text-xs font-mono">{x.s !== null ? x.s.toFixed(0) : '—'}</div>
                </div>
              ))}
            </div>
            {data.performance.wasteCostSar > 0 && (
              <div className="text-xs text-amber-700 dark:text-amber-400 inline-flex items-center gap-1 pt-1">
                <TrendingDown className="h-3 w-3" /> {fmtSar(data.performance.wasteCostSar)} attributed waste
              </div>
            )}
          </div>
        )}

        {/* This month */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">This month</div>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Trips"     value={String(data.thisMonth.trips)} />
            <Stat label="Distance"  value={data.thisMonth.totalKm.toFixed(0) + ' km'} />
            <Stat label="Delivered" value={data.thisMonth.deliveredQty.toFixed(1) + ' m³'} />
            <Stat label="Earnings"  value={fmtSar(data.thisMonth.driverPaySar)} highlight />
          </div>
        </div>

        {/* Recent trips */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Recent trips</div>
          {data.recentTrips.length === 0 ? (
            <div className="rounded-lg border border-border p-4 text-center text-sm text-muted-foreground">No trips yet.</div>
          ) : (
            <div className="space-y-1">
              {data.recentTrips.map((t) => (
                <div key={t.id} className="rounded-lg border border-border p-3 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{fmtDate(t.tripDate)}</span>
                      <span className="font-mono text-xs">{t.code}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                      {t.originSite ? t.originSite + ' → ' : ''}{t.destinationSite ?? '—'}
                    </div>
                    <div className="text-xs mt-0.5">
                      {t.deliveredQty.toFixed(1)} {t.unit}
                      {t.kmAllocated !== null && <span className="text-muted-foreground"> · {t.kmAllocated.toFixed(0)} km</span>}
                    </div>
                  </div>
                  {t.variancePct !== null && (
                    <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ' + variancePctTone(t.variancePct)}>
                      {t.variancePct > 0 ? '+' : ''}{t.variancePct.toFixed(0)}%
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Personal records */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Personal records (last 90d)</div>
          <div className="rounded-lg border border-border p-3 space-y-2">
            <RecordRow icon={<Award className="h-4 w-4 text-emerald-600" />} label="Best fuel efficiency" value={data.personalRecords.bestEfficiency !== null ? data.personalRecords.bestEfficiency.toFixed(1) + ' L/100km' : '—'} sub={data.personalRecords.bestEfficiencyTrip ?? undefined} />
            <RecordRow icon={<Truck className="h-4 w-4 text-sky-600" />}       label="Longest trip"      value={data.personalRecords.longestKm !== null ? data.personalRecords.longestKm.toFixed(0) + ' km' : '—'}     sub={data.personalRecords.longestKmTrip ?? undefined} />
            <RecordRow icon={<Calendar className="h-4 w-4 text-amber-600" />}  label="Most trips in a day" value={String(data.personalRecords.mostTripsInDay)} />
          </div>
        </div>

        {/* Leaderboard preview */}
        {data.leaderboard && (
          <Link to="/dashboard/fuel/driver-leaderboard" className="block rounded-lg border border-border p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Trophy className="h-5 w-5 text-amber-500" />
                <div>
                  <div className="text-sm font-medium">See full leaderboard</div>
                  <div className="text-xs text-muted-foreground">
                    {data.publicLeaderboard ? 'All drivers visible' : 'Anonymized — only your name shown'}
                  </div>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </Link>
        )}
      </main>
    </div>
  );
}

function barColor(s: number | null): string {
  if (s === null) return 'bg-muted';
  if (s >= 75) return 'bg-emerald-500';
  if (s >= 60) return 'bg-amber-500';
  if (s >= 40) return 'bg-orange-500';
  return 'bg-rose-500';
}

function variancePctTone(p: number): string {
  if (p < 10) return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
  if (p < 25) return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
  return 'bg-rose-500/15 text-rose-700 dark:text-rose-400';
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={'bg-muted rounded-lg p-3 ' + (highlight ? 'border border-emerald-500/30' : '')}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={'text-xl font-medium mt-0.5 ' + (highlight ? 'text-emerald-700 dark:text-emerald-400' : '')}>{value}</div>
    </div>
  );
}

function RecordRow({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      {icon}
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        {sub && <div className="text-[10px] text-muted-foreground font-mono">{sub}</div>}
      </div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
